<template>
    <div v-if="supported" class="ai-rec">
        <!-- IDLE -->
        <template v-if="!state && !hideStart">
            <div class="ai-btn ai-btn-ghost" @click="start()">
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M12 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6zm0-2a5 5 0 1 1 0 10 5 5 0 0 1 0-10zm10-3H2a1 1 0 0 0-1 1v13a1 1 0 0 0 1 1h6v2H6v2h12v-2h-2v-2h6a1 1 0 0 0 1-1V5a1 1 0 0 0-1-1zm-1 13H3V6h18v11z"/></svg>
                {{ audioOnly ? 'Record meeting' : 'Record your screen' }}
            </div>
            <label v-if="audioOnly && !state" class="ai-rec-hint"><input type="checkbox" v-model="shareAudio" /> Include audio from a shared tab or screen</label>
            <div v-if="!audioOnly" class="ai-rec-mic-toggle" :class="{ 'ai-rec-mic-off': !micEnabled }" :title="micEnabled ? 'Narration mic is on — click to record without it' : 'Narration mic is off — click to record with it'" @click="micEnabled = !micEnabled">
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5.91-3c-.49 0-.9.36-.98.85C16.52 14.2 14.47 16 12 16s-4.52-1.8-4.93-4.15c-.08-.49-.49-.85-.98-.85-.61 0-1.09.54-1 1.14.49 3 2.89 5.35 5.91 5.78V20c0 .55.45 1 1 1s1-.45 1-1v-2.08c3.02-.43 5.42-2.78 5.91-5.78.1-.6-.39-1.14-1-1.14z"/></svg>
                {{ micEnabled ? 'Mic on' : 'Mic off' }}
            </div>
            <div v-if="!audioOnly" class="ai-rec-hint">Choose <strong>Entire Screen</strong> in the picker so everything you do — other apps and tabs included — is captured.</div>
        </template>

        <!-- RECORDING -->
        <div v-else-if="state" class="ai-rec-pill">
            <span class="ai-rec-dot" :class="{ 'ai-rec-dot-paused': paused }"></span>
            <span class="ai-rec-time" :class="{ 'ai-rec-time-warn': elapsedSec >= 1080 }">{{ paused ? 'Paused' : (streaming ? 'Recording · ' : '') + timeLabel }}</span>
            <span v-if="micDenied" class="ai-rec-note" title="Microphone unavailable — your voice may not be included">no mic</span>
            <div class="ai-rec-discard" :title="paused ? 'Resume recording' : 'Pause recording'" @click="togglePause()">
                <svg v-if="!paused" xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M10 24h-6v-24h6v24zm10-24h-6v24h6v-24z"/></svg>
                <svg v-else xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M3 22v-20l18 10-18 10z"/></svg>
            </div>
            <div class="ai-rec-stop" title="Stop recording" @click="stop()">
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M4 4h16v16H4z"/></svg>
                Stop
            </div>
            <div v-if="!streaming" class="ai-rec-discard" title="Discard recording" @click="discardWhileRecording()">
                <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M24 20.188l-8.315-8.209 8.2-8.282-3.697-3.697-8.212 8.318-8.31-8.203-3.666 3.666 8.321 8.24-8.206 8.313 3.666 3.666 8.237-8.318 8.285 8.203z"/></svg>
            </div>
        </div>
        <label v-if="canFloat && !state && !guard" class="ai-rec-float-pref"><input type="checkbox" v-model="autoFloat" @change="saveAutoFloat" />Float the controls automatically</label>
        <!-- The shared screen came back without sound: ask before recording (see beginCapture()) -->
        <div v-if="guard" class="ai-rec-guard" role="alertdialog" aria-labelledby="ai-rec-guard-title" aria-describedby="ai-rec-guard-text">
            <strong id="ai-rec-guard-title" class="ai-rec-guard-title">No call audio</strong>
            <p id="ai-rec-guard-text">Only your microphone will be recorded. People on the call won’t be heard.</p>
            <div class="ai-rec-guard-actions">
                <button type="button" class="ai-rec-guard-primary" :disabled="guard.busy" @click="shareAgain">{{ guard.busy ? 'Waiting for Chrome’s picker…' : 'Share again with sound' }}</button>
                <button type="button" class="ai-rec-guard-secondary" :disabled="guard.busy" @click="continueWithoutAudio">Continue — we’re all in the room</button>
            </div>
            <AiShareGuide v-if="guard.busy || guard.attempts >= 2" class="ai-rec-guide-body" :note="guard.attempts >= 2 ? 'Chrome shows the toggle under the screen preview.' : ''" />
        </div>
        <details v-else-if="streaming && !audioOnly && !state" class="ai-rec-guide">
            <summary>How to include people on the call</summary>
            <AiShareGuide class="ai-rec-guide-body" />
        </details>
        <button v-if="streaming && !audioOnly" type="button" class="ai-rec-coach-link" @click="openCoachHelp">How do I include call audio?</button>
        <AiShareCoach v-if="coach" :mode="coach" @confirm="confirmCoach" @close="closeCoach" />
        <!-- Always mounted so Start can open its window inside the click (see beginCapture()) -->
        <AiFloatingRecorder ref="floating" :recording="!!state" :call-audio="streaming && !audioOnly" :guard="guard" @share-again="shareAgain" @continue="continueWithoutAudio" :display-stream="displayStream" :paused="paused" :time-label="timeLabel" :has-mic-audio="hasMicAudio" :has-shared-audio="hasSharedAudio" :mic-level="micLevel" @pause="togglePause" @stop="stop" />
        <div v-if="streaming && state" class="ai-rec-audio-status">
            <span>{{ hasMicAudio ? 'Microphone on' : 'Microphone not included' }}</span>
            <span v-if="hasMicAudio" class="ai-rec-level" role="meter" aria-label="Microphone input level" :aria-valuenow="Math.round(micLevel * 100)" aria-valuemin="0" aria-valuemax="100"><span :style="{ width: Math.round(micLevel * 100) + '%' }"></span></span>
            <span>{{ hasSharedAudio ? 'Computer audio included' : 'Computer audio not shared' }}</span>
            <p v-if="!hasSharedAudio">{{ hasMicAudio ? 'Your voice is included, but people on the call won’t be in the recording.' : 'Your microphone is not included, and people on the call won’t be in the recording.' }} Next time, choose <strong>Entire Screen</strong> and turn on <strong>Share with system audio</strong> in Chrome’s picker. A single window never shares sound. On Mac, Chrome may also need Screen &amp; System Audio Recording permission.</p>
        </div>
    </div>
    <p v-else class="ai-rec-hint">Screen recording requires a desktop browser. Open this meeting in Chrome or Edge on your computer.</p>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import AIFloatingRecorder from './AIFloatingRecorder.vue';
import AIShareGuide from './AIShareGuide.vue';
import AIShareCoach from './AIShareCoach.vue';

// Per-browser preference: open the floating controls by themselves on Start.
const AUTO_FLOAT_KEY = 'ironbook.recorder.autoFloat';
function readAutoFloat(): boolean {
    try { return localStorage.getItem(AUTO_FLOAT_KEY) !== '0'; } catch (e) { return true; }
}
// Meeting recordings that started WITH the call's sound in this browser (the permission coach shows
// until the first one).
const AUDIO_SHARES_KEY = 'ironbook.recorder.audioShares';
function readAudioShares(): number {
    try { return Number(localStorage.getItem(AUDIO_SHARES_KEY)) || 0; } catch (e) { return 0; }
}
// Whether this browser's last meeting recording had the call's sound ('1'/'0'), and the coach opt-out.
const LAST_SHARE_AUDIO_KEY = 'ironbook.recorder.lastShareAudio';
const COACH_OFF_KEY = 'ironbook.recorder.coachOff';
function readFlag(key: string): string | null {
    try { return localStorage.getItem(key); } catch (e) { return null; }
}
function writeFlag(key: string, value: string) {
    try { localStorage.setItem(key, value); } catch (e) { /* storage blocked: this page only */ }
}

// In-app screen recorder (Scribe-style capture): getDisplayMedia + optional mic
// narration mixed via AudioContext, recorded with MediaRecorder. Capture only —
// on stop it emits the blob ({ blob, durationSec, mimeType }) and returns to
// idle; AIProcessModal owns the preview/remove UI so the recording can never
// exist invisibly (e.g. after a failed submit re-mounts this component).
export default defineComponent({
    name: 'AIScreenRecorder',
    components: { AiFloatingRecorder: AIFloatingRecorder, AiShareGuide: AIShareGuide, AiShareCoach: AIShareCoach },
    props: { audioOnly: Boolean, streaming: Boolean, hideStart: Boolean },
    emits: ['recorded', 'recording', 'discard', 'chunk'],
    data() {
        return {
            state: null as any, // null | recording
            recorder: null as any,
            chunks: [] as any[],
            displayStream: null as any,
            micStream: null as any,
            audioCtx: null as any,
            micAnalyser: null as any,
            micLevel: 0,
            hasMicAudio: false,
            hasSharedAudio: false,
            micEnabled: true,
            autoFloat: readAutoFloat(), // default ON where floating controls exist (desktop Chrome/Edge)
            audioShares: readAudioShares(),
            lastShareAudio: readFlag(LAST_SHARE_AUDIO_KEY), // '1' | '0' | null
            coachOff: readFlag(COACH_OFF_KEY) === '1',
            // Permission coach before Chrome's picker: null | 'start' (opened by Start) | 'help' (opened by the link)
            coach: null as null | string,
            coachResolve: null as null | ((result: any) => void),
            coachDismissed: false, // the last Start was cancelled at the coach (not a picker failure)
            // "No call audio" choice: null, or { attempts (shares without sound so far), busy (picker open) }
            guard: null as null | { attempts: number, busy: boolean },
            guardResolve: null as null | ((proceed: boolean) => void),
            guardWatch: null as null | (() => void), // re-arms "Stop sharing" handling after a re-share
            shareAudio: false,
            micDenied: false,
            cancelled: false,
            starting: false,
            startToken: 0, // bumped by teardown/unmount to invalidate an in-flight start()
            dead: false,
            startedAt: 0,
            elapsedSec: 0,
            paused: false,
            pausedAt: 0,
            timer: null as any
        }
    },
    computed: {
        // Meeting recordings until a share WITH the call's sound, and again after one without it
        needsCoach(): boolean {
            return this.streaming && !this.audioOnly && !this.coachOff && (this.audioShares === 0 || this.lastShareAudio === '0');
        },
        canFloat(): boolean { return !!(window as any).documentPictureInPicture?.requestWindow; },
        supported(): boolean {
            return !!(navigator.mediaDevices && (this.audioOnly ? navigator.mediaDevices.getUserMedia : (navigator.mediaDevices as any).getDisplayMedia));
        },
        timeLabel(): string {
            return `${Math.floor(this.elapsedSec / 60)}:${String(this.elapsedSec % 60).padStart(2, '0')}`;
        }
    },
    watch: {
        // Recording ended (stopped, failed to save, cancelled): the controls go with it
        state(now: any) { if (!now) (this.$refs.floating as any)?.closePanel(); }
    },
    unmounted() {
        this.dead = true;
        this.teardown();
    },
    methods: {
        pickMimeType(): string {
            const candidates = this.audioOnly ? ['audio/webm;codecs=opus', 'audio/webm'] : [
                'video/webm;codecs=vp9,opus',
                'video/webm;codecs=vp8,opus',
                'video/webm',
                'video/mp4'
            ];
            for (const c of candidates) {
                if ((window as any).MediaRecorder && MediaRecorder.isTypeSupported(c)) return c;
            }
            return '';
        },
        stale(token: number): boolean {
            return this.dead || token !== this.startToken;
        },
        saveAutoFloat() {
            try { localStorage.setItem(AUTO_FLOAT_KEY, this.autoFloat ? '1' : '0'); } catch (e) { /* private window: keep for this page only */ }
        },
        displayConstraints(): any {
            return {
                video: { frameRate: { ideal: 5, max: 10 }, displaySurface: 'monitor' },
                audio: true,
                selfBrowserSurface: 'exclude', // hide the Playbook tab from the picker
                surfaceSwitching: 'include',
                systemAudio: 'include' // Windows: offer the "share system audio" checkbox
            };
        },
        // Meeting shared without sound: wait for "Share again with sound" (which swaps in a new
        // stream with audio) or "Continue". false = abandon the start (shared screen ended, unmount).
        askAboutMissingAudio(): Promise<boolean> {
            return new Promise(resolve => {
                const watchEnd = () => {
                    const track = this.displayStream?.getVideoTracks()[0];
                    if (track) track.onended = () => this.resolveGuard(false); // native "Stop sharing" during the choice
                };
                this.guardWatch = watchEnd;
                this.guardResolve = resolve;
                this.guard = { attempts: 1, busy: false };
                watchEnd();
            });
        },
        resolveGuard(proceed: boolean) {
            const resolve = this.guardResolve;
            this.guard = null;
            this.guardResolve = null;
            this.guardWatch = null;
            if (resolve) resolve(proceed);
        },
        // From the button click (a fresh user gesture, in the page or the floating panel): ask again.
        // The first stream is kept until a new share arrives, so cancelling this picker simply
        // returns to the same choice and "Continue" still records what was shared first.
        shareAgain() {
            const guard = this.guard;
            if (!guard || guard.busy) return;
            guard.busy = true;
            let pending: Promise<any>;
            try { pending = (navigator.mediaDevices as any).getDisplayMedia(this.displayConstraints()); } catch (e) { pending = Promise.reject(e); }
            pending.then((stream: MediaStream) => {
                if (this.guard !== guard) { stream.getTracks().forEach(t => t.stop()); return; } // start abandoned meanwhile
                const old = this.displayStream;
                if (old) old.getTracks().forEach((t: any) => { t.onended = null; t.stop(); });
                this.displayStream = stream;
                if (stream.getAudioTracks().length) { this.resolveGuard(true); return; }
                guard.attempts++;
                if (this.guardWatch) this.guardWatch();
            }, () => { /* picker cancelled: back to the same choice */ })
            .finally(() => { if (this.guard === guard) guard.busy = false; });
        },
        continueWithoutAudio() {
            if (this.guard && !this.guard.busy) this.resolveGuard(true);
        },
        // Start click. When the coach is needed it opens first, and its "Got it — open the picker"
        // click runs beginCapture() (a fresh gesture); otherwise capture begins inside this click
        // exactly as before. Resolves with beginCapture()'s result, or undefined if cancelled.
        start(): Promise<any> {
            if (this.state || this.starting || this.coach) return Promise.resolve();
            this.coachDismissed = false;
            if (this.needsCoach) return new Promise(resolve => { this.coachResolve = resolve; this.coach = 'start'; });
            return this.beginCapture();
        },
        confirmCoach({ dontShow }: { dontShow?: boolean } = {}) {
            if (dontShow) { this.coachOff = true; writeFlag(COACH_OFF_KEY, '1'); }
            const mode = this.coach, resolve = this.coachResolve;
            this.coach = null;
            this.coachResolve = null;
            // Synchronously inside the button's click: floating window + picker need its activation
            if (mode === 'start' && resolve) resolve(this.beginCapture());
        },
        closeCoach() {
            const mode = this.coach, resolve = this.coachResolve;
            this.coach = null;
            this.coachResolve = null;
            if (mode === 'start') { this.coachDismissed = true; if (resolve) resolve(undefined); }
        },
        openCoachHelp() {
            if (!this.coach) this.coach = 'help';
        },
        async beginCapture() {
            if (this.state || this.starting) return;
            // FIRST, before any await: the floating window needs (and consumes) this
            // click's user activation. getDisplayMedia below still works afterwards
            // because it shows its own picker; the other order fails in Chrome.
            const floated = this.autoFloat && !!(this.$refs.floating as any)?.openNow();
            this.starting = true;
            const token = this.startToken;
            let endedEarly = false;
            try {
                try {
                    this.displayStream = this.audioOnly && !this.shareAudio ? new MediaStream() : await (navigator.mediaDevices as any).getDisplayMedia(this.displayConstraints());
                } catch (e) {
                    if (!this.audioOnly) return; // screen capture cancelled
                    this.displayStream = new MediaStream(); // meeting can still use the mic
                }
                if (this.stale(token)) { this.stopAllTracks(); return; }
                // Meeting recording shared without the call's sound: don't start yet, ask first.
                // ("Continue" holds for this recording only.)
                if (this.streaming && !this.audioOnly && !this.displayStream.getAudioTracks().length) {
                    const proceed = await this.askAboutMissingAudio();
                    if (!proceed || this.stale(token)) { this.stopAllTracks(); return; }
                }
                // Attach BEFORE the mic prompt: the native "Stop sharing" button must
                // never be missed, even while a permission dialog is still open.
                const videoTrack = this.displayStream.getVideoTracks()[0];
                if (videoTrack && !this.audioOnly) videoTrack.onended = () => {
                    if (this.recorder && this.recorder.state != 'inactive') this.stop();
                    else endedEarly = true;
                };
                this.micDenied = false;
                this.micStream = null;
                if (this.micEnabled) {
                    let mic: any = null;
                    try {
                        mic = await navigator.mediaDevices.getUserMedia({ audio: true });
                    } catch (e) {
                        this.micDenied = true; // keep going with screen only
                    }
                    if (this.stale(token)) {
                        if (mic) mic.getTracks().forEach((t: any) => t.stop());
                        this.stopAllTracks();
                        return;
                    }
                    this.micStream = mic;
                }
                if (endedEarly) { this.stopAllTracks(); return; }
                // Everything below can throw (Web Audio, MediaRecorder ctor/start) —
                // one catch releases every live track so nothing keeps capturing.
                try {
                    const tracks: any[] = this.audioOnly ? [] : [videoTrack];
                    const displayAudio = this.displayStream.getAudioTracks();
                    const micAudio = this.micStream ? this.micStream.getAudioTracks() : [];
                    this.hasMicAudio = micAudio.length > 0;
                    this.hasSharedAudio = displayAudio.length > 0;
                    if (displayAudio.length || micAudio.length) {
                        this.audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
                        const dest = this.audioCtx.createMediaStreamDestination();
                        if (displayAudio.length) this.audioCtx.createMediaStreamSource(new MediaStream(displayAudio)).connect(dest);
                        if (micAudio.length) {
                            this.micAnalyser = this.audioCtx.createAnalyser();
                            this.micAnalyser.fftSize = 256;
                            this.audioCtx.createMediaStreamSource(new MediaStream(micAudio)).connect(this.micAnalyser);
                            this.micAnalyser.connect(dest);
                        }
                        await this.audioCtx.resume();
                        tracks.push(dest.stream.getAudioTracks()[0]);
                    }
                    if (this.audioOnly) {
                        if (!tracks.length) throw new Error('No audio source');
                        this.displayStream.getVideoTracks().forEach((t: any) => t.stop());
                    }
                    if (this.streaming && !displayAudio.length && !micAudio.length) throw new Error('No audio source');
                    const mimeType = this.pickMimeType();
                    this.recorder = new MediaRecorder(new MediaStream(tracks), {
                        ...(mimeType ? { mimeType } : {}),
                        videoBitsPerSecond: this.streaming ? 1000000 : 1500000,
                        audioBitsPerSecond: 64000
                    });
                    this.chunks = [];
                    this.cancelled = false;
                    this.recorder.ondataavailable = (e: any) => { if (e.data.size) { if (this.streaming) this.$emit('chunk', e.data); else this.chunks.push(e.data); } };
                    this.recorder.onstop = () => this.onStopped();
                    this.recorder.start(this.streaming ? 60000 : 1000); // 1s timeslice so long recordings accumulate incrementally
                } catch (e) {
                    this.recorder = null;
                    this.stopAllTracks();
                    alert(this.audioOnly ? 'Meeting recording could not start. Allow microphone access and try again.' : 'Screen recording could not start in this browser.');
                    return;
                }
                this.startedAt = Date.now();
                this.elapsedSec = 0;
                this.paused = false;
                this.timer = setInterval(() => {
                    const audible = (stream: MediaStream | null) => !!stream?.getAudioTracks().some(t => t.readyState === 'live' && t.enabled && !t.muted);
                    this.hasMicAudio = audible(this.micStream);
                    this.hasSharedAudio = audible(this.displayStream);
                    if (this.paused) { this.micLevel = 0; return; } // clock stops while paused
                    this.elapsedSec = Math.round((Date.now() - this.startedAt) / 1000);
                    if (this.micAnalyser && this.hasMicAudio) {
                        const samples = new Uint8Array(this.micAnalyser.fftSize);
                        this.micAnalyser.getByteTimeDomainData(samples);
                        const rms = Math.sqrt(samples.reduce((sum, n) => sum + Math.pow((n - 128) / 128, 2), 0) / samples.length);
                        this.micLevel = Math.min(1, rms * 5);
                    } else { this.micLevel = 0; }
                    if (!this.streaming && !this.audioOnly && this.elapsedSec >= 1200) this.stop(); // 20-min cap (server enforces too)
                }, 500);
                window.addEventListener('beforeunload', this.warnUnload);
                if (this.streaming && this.hasSharedAudio) {
                    this.audioShares++;
                    try { localStorage.setItem(AUDIO_SHARES_KEY, String(this.audioShares)); } catch (e) { /* storage blocked: guide stays open */ }
                }
                if (this.streaming && !this.audioOnly) {
                    // Started without the call's sound ("Continue"): coach again next time
                    this.lastShareAudio = this.hasSharedAudio ? '1' : '0';
                    writeFlag(LAST_SHARE_AUDIO_KEY, this.lastShareAudio);
                }
                this.state = 'recording';
                this.$emit('recording', true);
                return true;
            } finally {
                this.starting = false;
                // Picker cancelled, mic/recorder failure or teardown: no "Starting…" panel left behind
                if (floated && !this.state) (this.$refs.floating as any)?.closePanel();
            }
        },
        togglePause() {
            if (!this.recorder) return;
            if (this.recorder.state === 'recording') {
                this.recorder.pause();
                this.paused = true;
                this.pausedAt = Date.now();
            } else if (this.recorder.state === 'paused') {
                this.recorder.resume();
                // shift the clock forward so paused time doesn't count
                this.startedAt += Date.now() - this.pausedAt;
                this.paused = false;
            }
        },
        stop() {
            if (this.recorder && this.recorder.state != 'inactive') this.recorder.stop();
        },
        discardWhileRecording() {
            this.cancelled = true;
            this.stop();
        },
        onStopped() {
            if (this.paused) { this.startedAt += Date.now() - this.pausedAt; this.paused = false; }
            const precise = (Date.now() - this.startedAt) / 1000;
            const wasRecording = this.state == 'recording';
            const recorder = this.recorder;
            if (this.timer) { clearInterval(this.timer); this.timer = null; }
            window.removeEventListener('beforeunload', this.warnUnload);
            this.stopAllTracks();
            this.state = null;
            this.recorder = null;
            if (wasRecording) this.$emit('recording', false);
            if (this.cancelled || this.dead) {
                this.chunks = [];
                this.cancelled = false;
                if (!this.dead) this.$emit('discard');
                return;
            }
            if (this.streaming) { this.$emit('recorded', { durationSec: precise, mimeType: recorder?.mimeType || 'audio/webm' }); return; }
            const blob = new Blob(this.chunks, { type: recorder?.mimeType || 'video/webm' });
            this.chunks = [];
            if (!blob.size) { this.$emit('discard'); return; }
            if (blob.size > 300 * 1024 * 1024) {
                alert('Recordings up to 300 MB are supported — try a shorter recording.');
                this.$emit('discard');
                return;
            }
            this.$emit('recorded', { blob, durationSec: precise, mimeType: blob.type });
        },
        stopAllTracks() {
            for (const stream of [this.displayStream, this.micStream]) {
                if (stream) stream.getTracks().forEach((t: any) => { t.onended = null; t.stop(); });
            }
            this.displayStream = null;
            this.micStream = null;
            this.micAnalyser = null; this.micLevel = 0;
            if (this.audioCtx) { this.audioCtx.close().catch(() => { /* already closed */ }); this.audioCtx = null; }
        },
        teardown() {
            this.startToken++; // any in-flight start() bails at its next staleness check
            if (this.guard) this.resolveGuard(false);
            if (this.coach) this.closeCoach();
            if (this.timer) { clearInterval(this.timer); this.timer = null; }
            window.removeEventListener('beforeunload', this.warnUnload);
            if (this.recorder && this.recorder.state != 'inactive') {
                this.cancelled = true;
                try { this.recorder.stop(); } catch (e) { /* already stopped */ }
            }
            this.stopAllTracks();
        },
        warnUnload(e: any) {
            e.preventDefault();
            e.returnValue = '';
        }
    }
});
</script>

<style scoped>
.ai-rec-audio-status { flex-basis:100%; display:flex; align-items:center; gap:8px; flex-wrap:wrap; font-size:12px; color:#5e4938; }
.ai-rec-audio-status p { flex-basis:100%; margin:0; line-height:1.5; max-width:440px; }
.ai-rec-level { width:55px; height:7px; border-radius:5px; background:#e2d6ca; overflow:hidden; }
.ai-rec-level span { display:block; height:100%; background:#218643; transition:width .2s; }
.ai-rec { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.ai-rec-hint { flex-basis: 100%; font-size: 12px; color: #8a7f75; }
.ai-rec-guard { flex-basis: 100%; max-width: 560px; background: #fff; border: 1px solid #f0b48a; border-left: 4px solid #c2410c; border-radius: 10px; padding: 12px 14px; color: #34261c; }
.ai-rec-guard-title { display: block; font-size: 15px; }
.ai-rec-guard p { margin: 4px 0 10px; font-size: 13px; line-height: 1.45; }
.ai-rec-guard-actions { display: flex; flex-wrap: wrap; gap: 8px; }
.ai-rec-guard-actions button { min-height: 36px; padding: 0 14px; border-radius: 8px; font: inherit; font-size: 13px; font-weight: 650; cursor: pointer; }
.ai-rec-guard-primary { background: #c2410c; border: 1px solid #c2410c; color: #fff; }
.ai-rec-guard-primary:hover:not(:disabled) { background: #a8380a; }
.ai-rec-guard-secondary { background: #fff; border: 1px solid #d5bca7; color: #5e4938; }
.ai-rec-guard-secondary:hover:not(:disabled) { background: #fff4ea; }
.ai-rec-guard-actions button:disabled { opacity: .65; cursor: wait; }
.ai-rec-guard-actions button:focus-visible { outline: 2px solid #ad4c0c; outline-offset: 2px; }
.ai-rec-guide { flex-basis: 100%; font-size: 12px; color: #5e4938; }
.ai-rec-guide summary { cursor: pointer; font-weight: 650; }
.ai-rec-guide-body { margin-top: 8px; max-width: 360px; }
.ai-rec-coach-link { flex-basis: 100%; text-align: left; padding: 0; border: 0; background: none; font: inherit; font-size: 12px; font-weight: 600; color: #9a3d0b; text-decoration: underline; cursor: pointer; }
.ai-rec-coach-link:hover { color: #73380d; }
.ai-rec-coach-link:focus-visible { outline: 2px solid #ad4c0c; outline-offset: 2px; border-radius: 3px; }
.ai-rec-float-pref { display: inline-flex; align-items: center; gap: 6px; font-size: 12px; font-weight: 600; color: #5e4938; cursor: pointer; }
.ai-rec-float-pref input { width: 14px; height: 14px; margin: 0; accent-color: var(--primary-color); cursor: pointer; }
.ai-rec-mic-toggle {
    display: inline-flex; align-items: center; gap: 4px; cursor: pointer;
    font-size: 12px; font-weight: 600; color: var(--ai-accent);
    background: #fff4ea; border: 1px solid var(--ai-border-soft); border-radius: 14px; padding: 3px 10px;
    transition: all .2s ease;
}
.ai-rec-mic-toggle:hover { background: #ffedd5; }
.ai-rec-mic-off { color: #8a7f75; background: #f5f2ef; text-decoration: line-through; }
.ai-rec-pill {
    display: flex; align-items: center; gap: 10px;
    background: rgba(30, 20, 12, .93); border-radius: 26px; padding: 6px 8px 6px 14px;
    box-shadow: 0 8px 28px rgba(30, 20, 12, .45);
}
.ai-rec-dot {
    width: 10px; height: 10px; border-radius: 50%; background: #f43f5e;
    animation: ai-rec-pulse 1.2s ease-in-out infinite;
}
.ai-rec-dot-paused { animation: none; background: #fbbf24; }
@keyframes ai-rec-pulse {
    0%, 100% { opacity: 1; box-shadow: 0 0 0 0 rgba(244, 63, 94, .55); }
    50% { opacity: .65; box-shadow: 0 0 0 5px rgba(244, 63, 94, 0); }
}
.ai-rec-time { color: #fff; font-weight: 700; font-size: 13px; font-variant-numeric: tabular-nums; min-width: 38px; }
.ai-rec-time-warn { color: #fbbf24; }
.ai-rec-note { color: rgba(255, 255, 255, .55); font-size: 11px; }
.ai-rec-stop {
    display: flex; align-items: center; gap: 5px; cursor: pointer;
    background: var(--ai-grad); color: #fff; font-size: 12.5px; font-weight: 700;
    border-radius: 16px; padding: 5px 12px; transition: transform .2s ease;
    box-shadow: 0 2px 10px rgba(234, 88, 12, .5);
}
.ai-rec-stop:hover { transform: scale(1.05); }
.ai-rec-discard {
    width: 26px; height: 26px; border-radius: 50%; cursor: pointer;
    display: flex; align-items: center; justify-content: center;
    background: rgba(255, 255, 255, .18); color: #fff; transition: all .2s ease;
}
.ai-rec-discard:hover { background: rgba(255, 255, 255, .3); }
</style>
